import React, { useState } from 'react';
import { useGlobalization } from '@Translations';
import {
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Box,
} from '@mui/material';
import { StylovyzeComposableDialog, Button, Text } from '@innovyze/stylovyze';

interface AdjustCostDialogProps {
  open: boolean;
  onClose: () => void;
  onAdjustCostHandler: (percentage: number, isIncrease: boolean) => void;
}

const AdjustCostDialog: React.FC<AdjustCostDialogProps> = ({
  open,
  onClose,
  onAdjustCostHandler,
}) => {
  const { t } = useGlobalization();
  const [adjustmentType, setAdjustmentType] = useState<'increase' | 'decrease'>(
    'increase'
  );
  const [percentage, setPercentage] = useState<number>(20);

  const handleSubmit = () => {
    if (percentage !== undefined && percentage > 0) {
      onAdjustCostHandler(percentage, adjustmentType === 'increase');
      onClose();
    }
  };

  const handleCancel = () => {
    setPercentage(20);
    setAdjustmentType('increase');
    onClose();
  };

  return (
    <StylovyzeComposableDialog
      open={open}
      onCancel={handleCancel}
      title={t('Adjust Costs (by %)')}
      width="500px"
      dialogProps={{
        sx: {
          '& .MuiDialog-paper': {
            maxWidth: { xs: '90vw', sm: '500px' },
            width: '100%',
            margin: { xs: 1, sm: 3 },
          },
        },
      }}
      footer={
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 2 }}>
          <Button onClick={handleCancel} color="secondary">
            {t('Cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={percentage === undefined || percentage <= 0}>
            {t('Update')}
          </Button>
        </Box>
      }>
      <Box sx={{ py: 2 }}>
        <RadioGroup
          value={adjustmentType}
          onChange={e =>
            setAdjustmentType(e.target.value as 'increase' | 'decrease')
          }>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 2,
            }}>
            <FormControlLabel
              value="increase"
              control={<Radio />}
              label=""
              sx={{ mr: 1 }}
            />
            <Text variant="text-medium" sx={{ mr: 6, minWidth: '120px' }}>
              {t('Increase by: ')}
            </Text>
            <TextField
              size="small"
              value={adjustmentType === 'increase' ? percentage || '' : ''}
              onChange={e => setPercentage(Number(e.target.value))}
              disabled={adjustmentType !== 'increase'}
              sx={{ width: 80, mr: 3, ml: 1 }}
              inputProps={{
                style: { textAlign: 'center' },
                min: 0,
                step: 0.1,
              }}
            />
            <Text variant="text-medium">%</Text>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              value="decrease"
              control={<Radio />}
              label=""
              sx={{ mr: 1 }}
            />
            <Text variant="text-medium" sx={{ mr: 6, minWidth: '120px' }}>
              {t('Decrease by: ')}
            </Text>
            <TextField
              size="small"
              value={adjustmentType === 'decrease' ? percentage || '' : ''}
              onChange={e => setPercentage(Number(e.target.value))}
              disabled={adjustmentType !== 'decrease'}
              sx={{ width: 80, mr: 3, ml: 1 }}
              inputProps={{
                style: { textAlign: 'center' },
                min: 0,
                step: 0.1,
              }}
            />
            <Text variant="text-medium">%</Text>
          </Box>
        </RadioGroup>
      </Box>
    </StylovyzeComposableDialog>
  );
};

export default AdjustCostDialog;
